// src/pages/Revenue.js
//
// ═══════════════════════════════════════════════════════════════════════════
//  CHANGE LOG — 2026-09-18
//
//  1. FOUR FAKE CHART METRICS REMOVED.
//       buildPerfData() used to map them like this:
//           unique_click:    parseInt(h.clicks || 0),   // <- a COPY of clicks
//           duplicate_click: 0,
//           invalid_click:   0,
//           event:           0,
//       So picking "Unique Clicks" on the Performance Graph drew Total
//       Clicks in a different colour, and Duplicate Clicks / Invalid Clicks
//       / Events drew a flat line at zero that looked like a measurement.
//       Four of the eight options on that dropdown were not real. They are
//       gone from the selector AND from the data mapping — nothing is
//       copied or zero-filled any more.
//
//       (The Uniq. Clicks COLUMN in the table below is untouched. That one
//       reads real values out of revenue_cache. It shows 0 for the Cake
//       networks because Cake's CampaignSummary isn't returning it under the
//       field name services/cake.js reads — being investigated separately
//       with diag_cake_unique.js. Everflow rows show real numbers.)
//
//  2. CHART FAILURES ARE NOW VISIBLE.
//       The catch block did `setPerfData([])`, which rendered an empty chart
//       with normal-looking axes — indistinguishable from a genuinely quiet
//       day. Now a failure says so and offers Retry.
//
//  NOT CHANGED, deliberately, after checking with Aditi on 2026-09-18:
//    • The flat line along zero for hours that haven't happened yet. It is
//      a drawn line with no value behind it, and it reads fine in context.
//    • type="monotone" on the hourly chart. The tooltip always reports the
//      real data point (verified on screen: 15:00 -> Revenue $0.00, Total
//      Clicks 2), so the curve between points is smoothing, not a false
//      number. Switch to bars or stepAfter any time if the shape is wanted.
// ═══════════════════════════════════════════════════════════════════════════
//
// ═══════════════════════════════════════════════════════════════════════════
//  CHANGE LOG — 2026-09-23
//
//  THE PROBLEM (reported on desktop AND mobile):
//    Long Sub IDs and long Offer names are cut off by the column width and
//    there is no way to read the rest. The columns can't be dragged wider.
//    The Offer cell had title={r.offer_name}, which shows a browser tooltip
//    after about a second of HOVER — so it works with a mouse and does
//    nothing at all on a phone. The Sub ID cell had nothing.
//
//  THE FIX — tap to read the full value:
//    Tap (or click) a Sub ID or an Offer name and a small box opens showing
//    the whole value, with a Copy button. It closes when you
//      • tap anywhere else on the screen
//      • press Escape
//      • scroll the page or the table
//      • or wait 10 seconds — it closes itself
//
//    The box positions itself under the cell you tapped and flips above it
//    when there isn't room below, so it is never pushed off screen. It uses
//    the app's own theme variables, so it follows light/dark mode.
//
//  NOTHING ELSE ON THIS PAGE CHANGED. No filter, no fetch, no chart, no
//  column, no number. The only touched rows in the table are the two cells
//  named above, which gained an onClick and a pointer cursor.
// ═══════════════════════════════════════════════════════════════════════════

// CHANGED 2026-09-23: added useLayoutEffect — the popup has to measure its
// own real width/height BEFORE the browser paints it, so it can decide
// whether to sit below or above the cell. useEffect would paint once in the
// wrong place and then jump.
import { useState, useEffect, useLayoutEffect, useCallback, useRef } from "react";
import Sidebar from "../components/Sidebar";
import Header  from "../components/Header";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import {
  ComposedChart, BarChart, Bar, Area, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from "recharts";
import "../styles/revenue.css";
import {
  todayYMD,
  addDaysYMD,
  monthStartYMD,
  monthEndYMD,
  daysInMonth,
  firstWeekdayOfMonth,
  ymdToDisplay,
  edtTime,
} from "../utils/edt";

const API = process.env.REACT_APP_API_URL || "https://ipanel.idigitalhub.org/api";
// Smart-refresh interval: poll /sync/last-run every 15 sec.
// Re-fetch data only when the cron's last_run_at timestamp changed.
// (The Authorization header is set globally by AuthContext on login, so
// these calls are authenticated without passing headers here.)
const POLL_MS = 15 * 1000;

/* ─── Date Range Picker ─────────────────────────────────────────
   Everything here operates on YMD strings ("2026-04-10"), not on
   Date objects. This guarantees that clicking a cell produces the
   same YMD value regardless of browser timezone.
   ─────────────────────────────────────────────────────────────── */
const PRESETS = [
  { label: "Today",        days: 0       },
  { label: "Yesterday",    days: -1      },
  { label: "Last 7 Days",  days: 6       },
  { label: "Last 30 Days", days: 29      },
  { label: "This Month",   type: "month" },
  { label: "Last Month",   type: "lastmonth" },
];

function DateRangePicker({ start, end, onChange, onClose }) {
  // "Today" as an EDT YMD string — this is the single source of truth.
  const todayStr = todayYMD();

  // Parse it once into numeric parts for calendar navigation.
  const [ty, tm, td] = todayStr.split("-").map(Number);
  const today = { year: ty, month: tm - 1, day: td };   // month 0-indexed internally

  const [viewYear,  setViewYear]  = useState(today.year);
  const [viewMonth, setViewMonth] = useState(today.month);
  const [sel,       setSel]       = useState({ start: start || null, end: end || null });
  const [hover,     setHover]     = useState(null);

  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const days   = ["Mo","Tu","We","Th","Fr","Sa","Su"];

  const applyPreset = p => {
    let s, e;
    if (p.type === "month") {
      // First of the current EDT month to today
      s = monthStartYMD(today.year, today.month + 1);
      e = todayStr;
    } else if (p.type === "lastmonth") {
      // Whole previous month in EDT
      const lmYear  = today.month === 0 ? today.year - 1 : today.year;
      const lmMonth = today.month === 0 ? 12             : today.month;  // 1-indexed
      s = monthStartYMD(lmYear, lmMonth);
      e = monthEndYMD(lmYear, lmMonth);
    } else if (p.days === 0) {
      s = e = todayStr;
    } else if (p.days === -1) {
      s = e = addDaysYMD(todayStr, -1);
    } else {
      s = addDaysYMD(todayStr, -p.days);
      e = todayStr;
    }
    setSel({ start: s, end: e });
  };

  const clickDay = dateStr => {
    if (!sel.start || sel.end) {
      setSel({ start: dateStr, end: null });
    } else {
      // String comparison works correctly for "YYYY-MM-DD" format
      const st = sel.start > dateStr ? dateStr  : sel.start;
      const en = sel.start > dateStr ? sel.start : dateStr;
      setSel({ start: st, end: en });
    }
  };

  const isInRange = dateStr => {
    if (!sel.start) return false;
    const endD = sel.end || hover;
    if (!endD) return false;
    const st = sel.start < endD ? sel.start : endD;
    const en = sel.start < endD ? endD      : sel.start;
    return dateStr >= st && dateStr <= en;
  };

  const renderMonth = (year, month) => {
    const daysInM  = daysInMonth(year, month);
    const firstDay = firstWeekdayOfMonth(year, month);
    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    // Each cell is just a {year, month, day} tuple — no Date object, no timezone.
    for (let d = 1; d <= daysInM; d++) cells.push({ year, month, day: d });

    return (
      <div className="drp-month">
        <div className="drp-month-title">{months[month].toUpperCase()} {year}</div>
        <div className="drp-days-header">{days.map(d => <span key={d}>{d}</span>)}</div>
        <div className="drp-days-grid">
          {cells.map((cell, i) => {
            if (!cell) return <span key={`e${i}`} className="drp-day empty"/>;
            // Build YMD directly from integers — zero timezone conversion.
            const str = `${cell.year}-${String(cell.month + 1).padStart(2,"0")}-${String(cell.day).padStart(2,"0")}`;
            const isStart = str === sel.start;
            const isEnd   = str === sel.end;
            const inRange = isInRange(str);
            const isToday = str === todayStr;
            return (
              <span key={str}
                className={`drp-day${isStart||isEnd?" sel":""}${inRange&&!isStart&&!isEnd?" in-range":""}${isToday?" today":""}`}
                onClick={() => clickDay(str)}
                onMouseEnter={() => sel.start && !sel.end && setHover(str)}
                onMouseLeave={() => setHover(null)}>
                {cell.day}
              </span>
            );
          })}
        </div>
      </div>
    );
  };

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };
  const nextY = viewMonth === 11 ? viewYear + 1 : viewYear;
  const nextM = viewMonth === 11 ? 0            : viewMonth + 1;

  const canApply = sel.start && sel.end;

  return (
    <div className="drp-popup" onClick={e => e.stopPropagation()}>
      <div className="drp-left">
        <div className="drp-presets-title">PRESETS</div>
        <div className="drp-presets-row">
          {PRESETS.map(p => (
            <button key={p.label} className="drp-preset" onClick={() => applyPreset(p)}>
              {p.label}
            </button>
          ))}
        </div>
      </div>
      <div className="drp-right">
        <div className="drp-calendars">
          <div className="drp-nav">
            <button className="drp-nav-btn" onClick={prevMonth}>‹</button>
          </div>
          {renderMonth(viewYear, viewMonth)}
          {renderMonth(nextY, nextM)}
          <div className="drp-nav">
            <button className="drp-nav-btn" onClick={nextMonth}>›</button>
          </div>
        </div>
        <div className="drp-footer">
          <span className="drp-range-txt">
            {sel.start ? ymdToDisplay(sel.start) : "Start date"}
            {" "}<span style={{color:"var(--text-muted)"}}>to</span>{" "}
            {sel.end   ? ymdToDisplay(sel.end)   : "End date"}
          </span>
          <div style={{display:"flex",gap:8}}>
            <button className="drp-cancel" onClick={onClose}>Cancel</button>
            <button className="drp-apply" disabled={!canApply}
              onClick={() => { onChange(sel.start, sel.end); onClose(); }}>
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Metric Selector ─────────────────────────
   ── CHANGED 2026-09-18 ──────────────────────────────────────────────────
   Was eight entries:
       revenue, total_click, unique_click, cv, cvr,
       duplicate_click, invalid_click, event
   Only four of them had real data behind them. `unique_click` was mapped
   to a copy of total clicks, and duplicate_click / invalid_click / event
   were hardcoded to 0 in buildPerfData() — so selecting any of those four
   produced a chart that looked like a measurement and wasn't one.

   The four that remain are all genuinely returned per-hour AND per-day by
   the backend, so every one of them is real on every date range.

   To bring the others back properly the backend has to return them per
   hour and per day — /revenue/chart already returns unique_clicks and
   events as of today, but /revenue/chart/hourly does not, and a metric
   that only works on some date ranges is worse than one that isn't there.
   ─────────────────────────────────────────────────────────────────────── */
const ALL_METRICS = [
  { key:"revenue",     label:"Revenue"      },
  { key:"total_click", label:"Total Clicks" },
  { key:"cv",          label:"CV"           },
  { key:"cvr",         label:"CVR"          },
];

function MetricSelector({ selected, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const toggle = key => {
    if (selected.includes(key)) {
      if (selected.length === 1) return; // keep at least 1
      onChange(selected.filter(k => k !== key));
    } else {
      if (selected.length >= 2) onChange([selected[1], key]);
      else onChange([...selected, key]);
    }
  };

  const labels = selected.map(k => ALL_METRICS.find(m => m.key === k)?.label || k).join(", ");

  return (
    <div className="metric-sel-wrap" ref={ref}>
      <button className="metric-sel-btn" onClick={() => setOpen(!open)}>
        {labels} <span style={{fontSize:11,opacity:0.6}}>{selected.length}/2</span> ▾
      </button>
      {open && (
        <div className="metric-sel-dd">
          <div className="metric-sel-hint">You can select up to 2 items</div>
          {ALL_METRICS.map(m => (
            <label key={m.key} className="metric-sel-item">
              <input type="checkbox"
                checked={selected.includes(m.key)}
                onChange={() => toggle(m.key)}/>
              {m.label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Perf Tooltip ──────────────────────────── */
function PerfTip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="ptip">
      <div className="ptip-hd">
        <span className="ptip-dt">{label}</span>
      </div>
      {payload.map((p, i) => (
        <div key={i} style={{display:"flex",justifyContent:"space-between",gap:16,fontSize:12,padding:"2px 0"}}>
          <span style={{color:p.color}}>{p.name}</span>
          <span style={{fontWeight:700,color:"var(--text-primary)"}}>
            {/* CHANGED 2026-09-18: CVR is a percentage, not a count.
                parseInt() used to chop 5.88% down to "5". */}
            {p.name === "Revenue" ? "$" + parseFloat(p.value).toFixed(2)
             : p.name === "CVR"   ? parseFloat(p.value).toFixed(2) + "%"
             : parseInt(p.value).toLocaleString()}
          </span>
        </div>
      ))}
    </div>
  );
}

const METRIC_COLORS = {
  revenue:"#D97736", total_click:"#5A94E6", cv:"#7C3AED", cvr:"#DC2626",
};

/* ═══════════════════════════════════════════════════════════════════════════
   ADDED 2026-09-23 — CellPopup
   The little box that shows the FULL text of a cut-off table cell.

   How it closes — all four ways, deliberately:
     1. tap/click anywhere else  → handled by the full-screen invisible
                                   overlay below, which swallows the tap so
                                   it doesn't also hit whatever is underneath
     2. Escape key               → keydown listener
     3. scrolling                → 'scroll' listener with capture=true, which
                                   is what catches scrolling INSIDE the
                                   table's own scroll box, not just the page
     4. after 10 seconds         → setTimeout, cleared on unmount

   Positioning: it renders off-screen first (top:-9999), then useLayoutEffect
   measures the real box and places it, clamped to the viewport. That happens
   before paint, so you never see it move.
   ═══════════════════════════════════════════════════════════════════════════ */
function CellPopup({ label, text, mono, anchor, onClose }) {
  const boxRef = useRef(null);
  // Start off-screen so the first paint can't flash in the wrong place.
  const [pos,    setPos]    = useState({ top: -9999, left: -9999 });
  const [copied, setCopied] = useState(false);

  // ── Place the box, now that its real size is known ──────────────────────
  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!box || !anchor) return;
    const bw = box.offsetWidth;
    const bh = box.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Horizontal: line up with the cell, then pull back inside the screen.
    let left = anchor.left;
    if (left + bw > vw - 10) left = vw - 10 - bw;
    if (left < 10) left = 10;

    // Vertical: below the cell; if that runs off the bottom, put it above.
    let top = anchor.bottom + 6;
    if (top + bh > vh - 10) top = anchor.top - 6 - bh;
    if (top < 10) top = 10;

    setPos({ top, left });
  }, [anchor]);

  // ── Close on Escape ─────────────────────────────────────────────────────
  useEffect(() => {
    const onKey = e => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // ── Close on any scroll or resize ───────────────────────────────────────
  // capture=true is the important bit: the table scrolls inside its own div,
  // and a scroll there does NOT bubble up to window without capture.
  useEffect(() => {
    const go = () => onClose();
    window.addEventListener("scroll", go, true);
    window.addEventListener("resize", go);
    return () => {
      window.removeEventListener("scroll", go, true);
      window.removeEventListener("resize", go);
    };
  }, [onClose]);

  // ── Close itself after 10 seconds ───────────────────────────────────────
  // `anchor` is a fresh object every time a cell is tapped, so opening a new
  // popup restarts this timer instead of inheriting the old one.
  useEffect(() => {
    const t = setTimeout(() => onClose(), 10000);
    return () => clearTimeout(t);
  // `anchor` is listed on purpose (it restarts the countdown for a new
  // cell); the linter calls that "unnecessary", so silence it here only.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchor, onClose]);

  // ── Copy button ─────────────────────────────────────────────────────────
  // navigator.clipboard is the modern way and works here (the panel is
  // HTTPS). The execCommand path is a fallback for older mobile browsers.
  const doCopy = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity  = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      /* copying is a convenience — never break the popup over it */
    }
  };

  return (
    <>
      {/* Invisible full-screen catcher. A tap anywhere lands HERE, closes
          the popup, and does not reach the page behind it. */}
      <div
        onClick={onClose}
        style={{ position:"fixed", inset:0, zIndex:9998, background:"transparent" }}
      />
      <div
        ref={boxRef}
        onClick={e => e.stopPropagation()}   /* taps inside must not close it */
        style={{
          position:"fixed",
          top: pos.top,
          left: pos.left,
          zIndex: 9999,
          maxWidth: "min(380px, calc(100vw - 24px))",
          minWidth: 180,
          background: "var(--bg-card, #fff)",
          color: "var(--text-primary, #111827)",
          border: "1px solid var(--border, #E5E7EB)",
          borderRadius: 8,
          boxShadow: "var(--shadow-lg, 0 10px 40px rgba(0,0,0,0.18))",
          padding: "10px 12px 9px",
        }}>
        <div style={{
          fontSize: 10, fontWeight: 700, letterSpacing: "0.6px",
          textTransform: "uppercase", color: "var(--text-muted, #9CA3AF)",
          marginBottom: 5,
        }}>
          {label}
        </div>
        <div style={{
          fontSize: 13,
          lineHeight: 1.45,
          fontWeight: 500,
          /* break-all so a 90-character Sub ID with no spaces still wraps */
          wordBreak: "break-all",
          fontFamily: mono
            ? "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace"
            : "inherit",
        }}>
          {text}
        </div>
        <div style={{ display:"flex", justifyContent:"flex-end", gap:8, marginTop:9 }}>
          <button
            onClick={doCopy}
            style={{
              padding:"4px 11px", borderRadius:6, fontSize:11.5, fontWeight:600,
              border:"1px solid var(--border, #E5E7EB)",
              background:"var(--bg-card2, #F8F9FA)",
              color:"var(--text-primary, #111827)",
              cursor:"pointer", fontFamily:"inherit",
            }}>
            {copied ? "Copied ✓" : "Copy"}
          </button>
          <button
            onClick={onClose}
            style={{
              padding:"4px 11px", borderRadius:6, fontSize:11.5, fontWeight:600,
              border:"1px solid transparent",
              background:"transparent",
              color:"var(--text-secondary, #6B7280)",
              cursor:"pointer", fontFamily:"inherit",
            }}>
            Close
          </button>
        </div>
      </div>
    </>
  );
}

/* ─── Sort Icon ─────────────────────────────── */
function SortIcon({ col, sortCol, sortDir }) {
  if (sortCol !== col) return <span style={{opacity:0.3,marginLeft:3}}>↕</span>;
  return <span style={{marginLeft:3,color:"var(--sidebar-active-color)"}}>{sortDir === "asc" ? "↑" : "↓"}</span>;
}

/* ─── Main Page ─────────────────────────────── */
const PG_OPTIONS = [15, 25, 50, 100];

export default function Revenue() {
  const { user } = useAuth();

  /* filters */
  const [range,       setRange]       = useState("today");
  const [customStart, setCustomStart] = useState("");
  const [customEnd,   setCustomEnd]   = useState("");
  const [showDRP,     setShowDRP]     = useState(false);
  const [groupBy,     setGroupBy]     = useState("offer");
  const [affiliateF,  setAffiliateF]  = useState("all");
  const [search,      setSearch]      = useState("");
  const [sortCol,     setSortCol]     = useState("revenue");
  const [sortDir,     setSortDir]     = useState("desc");
  const [pageSize,    setPageSize]    = useState(15);
  const [page,        setPage]        = useState(1);

  /* chart */
  const [selMetrics,  setSelMetrics]  = useState(["revenue","total_click"]);
  const [chartType,   setChartType]   = useState("line");

  /* data */
  const [data,        setData]        = useState([]);
  const [summary,     setSummary]     = useState({});
  const [perfData,    setPerfData]    = useState([]);
  const [perfError,   setPerfError]   = useState(null);   // ADDED 2026-09-18
  const [perfLoading, setPerfLoading] = useState(false);  // ADDED 2026-09-18
  const [affiliates,  setAffiliates]  = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState("");
  const [lastSync,    setLastSync]    = useState(null);   // cron's last_run_at timestamp

  const pollTimer  = useRef(null);
  const lastRunRef = useRef(null);  // tracks last seen cron timestamp to detect changes

  /* ── ADDED 2026-09-23 — state for the "show me the full value" popup ────
     null  = nothing open
     object= { label, text, mono, anchor } for the cell that was tapped   */
  const [cellPopup, setCellPopup] = useState(null);

  // Stable close function. Stable matters: CellPopup's Escape / scroll /
  // 10-second effects depend on it, and a new function on every render
  // would tear those listeners down and rebuild them constantly.
  const closeCellPopup = useCallback(() => setCellPopup(null), []);

  /* Open the popup for whichever cell was tapped.
     `anchor` is the cell's position on screen RIGHT NOW, read synchronously
     inside the handler — the popup uses it to place itself. */
  const openCellPopup = (e, label, value, mono) => {
    const text = value == null ? "" : String(value);
    if (!text.trim() || text === "—") return;   // nothing to show for blank rows
    const r = e.currentTarget.getBoundingClientRect();
    setCellPopup({
      label,
      text,
      mono: !!mono,
      anchor: { left: r.left, top: r.top, bottom: r.bottom },
    });
  };

  useEffect(() => {
    axios.get(`${API}/affiliates`).then(r => setAffiliates(r.data.affiliates || [])).catch(() => {});
  }, []);

  /*  Chart data — real per-day / per-hour from backend.
      - Single day (today / yesterday / custom where start == end):
          /revenue/chart/hourly  → 24 hour buckets
      - Anything else:
          /revenue/chart         → N per-day buckets

      Both endpoints return measured numbers for every active network as of
      2026-09-18 (services/cake.js grew getHourlyByDate() backed by Cake's
      Reports/HourlySummary; routes/revenue.js dropped the HOUR_WEIGHTS
      invention). Nothing here distributes, copies or zero-fills a value.

      CHANGED 2026-09-18: promoted to useCallback so the Retry button can
      call it directly, and it now records failures instead of silently
      emptying the chart.  */
  const buildPerfData = useCallback(async (rng, cs, ce) => {
    setPerfLoading(true);
    setPerfError(null);
    try {
      // Hourly views (single day): today, yesterday, or any custom range
      // where start == end. A single-day /chart response returns just one
      // point, which can't draw a line — so we switch to /chart/hourly
      // (24 hour buckets) for any single-day selection.
      const isCustomSingleDay = rng === "custom" && cs && ce && cs === ce;
      if (rng === "today" || rng === "yesterday" || isCustomSingleDay) {
        const date = rng === "today"     ? todayYMD()
                   : rng === "yesterday" ? addDaysYMD(todayYMD(), -1)
                   : cs;  // custom single-day uses the picked date
        const res = await axios.get(`${API}/revenue/chart/hourly`, { params: { date } });
        const hours = res.data?.data || [];
        // ── CHANGED 2026-09-18 ────────────────────────────────────────
        // Removed from this mapping:
        //     unique_click:    parseInt(h.clicks || 0),   // a COPY
        //     duplicate_click: 0,
        //     invalid_click:   0,
        //     event:           0,
        // Only fields the endpoint actually measures are mapped now.
        setPerfData(hours.map(h => ({
          time:        String(h.hour).padStart(2, "0") + ":00",
          revenue:     parseFloat((h.revenue || 0).toFixed(2)),
          total_click: parseInt(h.clicks || 0),
          cv:          parseInt(h.conversions || 0),
          cvr:         h.clicks > 0 ? parseFloat(((h.conversions / h.clicks) * 100).toFixed(2)) : 0,
        })));
        return;
      }

      // Multi-day views: this_week / last_week / month / last_month / custom (>1 day)
      const params = {};
      if (rng === "custom") {
        if (!cs || !ce) { setPerfData([]); return; }
        params.range = "custom";
        params.start = cs;
        params.end   = ce;
      } else {
        params.range = rng;
      }
      const res = await axios.get(`${API}/revenue/chart`, { params });
      const days = res.data?.data || [];
      // CHANGED 2026-09-18: same four fake keys removed here too.
      setPerfData(days.map(d => ({
        time:        d.date ? d.date.slice(5) : "",   // "MM-DD" compact label
        revenue:     parseFloat((d.revenue || 0).toFixed(2)),
        total_click: parseInt(d.clicks || 0),
        cv:          parseInt(d.conversions || 0),
        cvr:         d.clicks > 0 ? parseFloat(((d.conversions / d.clicks) * 100).toFixed(2)) : 0,
      })));
    } catch (e) {
      // ── CHANGED 2026-09-18 ──────────────────────────────────────────
      // Was: console.error(...) then setPerfData([]) — which drew an empty
      // chart with normal axes. That is indistinguishable from a genuinely
      // quiet day, so a broken request looked like "no traffic". Now the
      // page says the request failed.
      console.error("buildPerfData error:", e.message);
      setPerfData([]);
      setPerfError(e.response?.data?.message || e.message || "Request failed");
    } finally {
      setPerfLoading(false);
    }
  }, []);

  const fetchRevenue = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError("");
    try {
      const params = { range, groupBy, cached_only: 'true' };
      if (range === "custom") { params.start = customStart; params.end = customEnd; }
      if (affiliateF !== "all") params.affiliate_id = affiliateF;
      const res = await axios.get(`${API}/revenue`, { params });
      const rows = res.data.data || [];
      setData(rows);
      setSummary(res.data.summary || {});
      buildPerfData(range, customStart, customEnd);
    } catch (err) {
      if (!silent) setError(err.response?.data?.message || "Failed to fetch revenue.");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [range, customStart, customEnd, groupBy, affiliateF, buildPerfData]);

  /* ═══════════════════════════════════════════════════════════════════════
     ADDED 2026-09-19 — THE FIX FOR "THE PAGE KEEPS RESETTING MY FILTERS".

     THE PROBLEM, in plain terms:
       When this page opened it took a PHOTOGRAPH of your settings
       (today / by offer / all affiliates) and handed that photo to the
       auto-refresh below. Every time the background sync finished, the
       refresh reloaded the table FROM THE PHOTO — not from what was on
       your screen. So if you had since switched to Last Month / By Sub ID /
       CX3Ads, the page snapped back and your work vanished, with nothing
       to explain why.

     WHY IT HAPPENED (the technical bit, for whoever reads this next):
       `fetchRevenue` is a useCallback that is rebuilt whenever a filter
       changes. The polling effect below has `[]` dependencies, so it only
       ever ran once and captured the FIRST `fetchRevenue` — the one built
       on the very first render, holding the very first set of filters.

     THE FIX:
       A "box" (useRef) that always holds the CURRENT fetchRevenue. The
       effect below keeps it up to date on every render. The poller reads
       the box instead of the stale copy.

     WHAT DOES *NOT* CHANGE — this matters:
       • the poll still runs every 15 seconds
       • it still only reloads when the cron's timestamp actually changes
       • it is still a silent reload with no loading spinner
       • the interval is still created once and cleared once
       The refresh behaves identically. It simply refreshes the view you
       are actually looking at.
     ═══════════════════════════════════════════════════════════════════════ */
  const fetchRevenueRef = useRef(fetchRevenue);
  useEffect(() => { fetchRevenueRef.current = fetchRevenue; }, [fetchRevenue]);

  /* Smart-refresh: poll /sync/last-run every 15 sec.
     Re-fetch revenue data ONLY when the cron's last_run_at changes.
     This means the page updates within 15 sec of each cron completion
     without making unnecessary API calls when nothing has changed. */
  useEffect(() => {
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/revenue/sync/last-run`);
        const ts = res.data?.jobs?.["today-sync"]?.last_run_at;
        if (ts && ts !== lastRunRef.current) {
          lastRunRef.current = ts;
          setLastSync(new Date(ts));
          // CHANGED 2026-09-19: was `fetchRevenue(true)` — the frozen copy
          // from the first render, which is what reset your filters.
          // Reading through the ref always gets the current one.
          fetchRevenueRef.current(true);  // silent re-fetch — no loading spinner
        }
      } catch (e) { /* polling failures are non-fatal */ }
    };
    poll();  // check immediately on mount
    pollTimer.current = setInterval(poll, POLL_MS);
    return () => clearInterval(pollTimer.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { if (range !== "custom") { fetchRevenue(); setPage(1); } },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [range, groupBy, affiliateF]);

  const handleSort = col => {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("desc"); }
    setPage(1);
  };

  // Multi-term search — split by comma, all terms must match
  const filtered = data.filter(r => {
    if (!search.trim()) return true;
    const terms = search.split(",").map(t => t.trim().toLowerCase()).filter(Boolean);
    if (!terms.length) return true;
    const haystack = [r.offer_name || "", r.sub_id || "", r.affiliate_name || ""].join(" ").toLowerCase();
    return terms.every(t => haystack.includes(t));
  }).sort((a, b) => {
    const av = a[sortCol] ?? 0, bv = b[sortCol] ?? 0;
    const cmp = typeof av === "string" ? av.localeCompare(bv) : (av - bv);
    return sortDir === "asc" ? cmp : -cmp;
  });

  const totalPages = Math.ceil(filtered.length / pageSize);
  const paginated  = filtered.slice((page - 1) * pageSize, page * pageSize);
  const fmtMoney = v => "$" + parseFloat(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtNum   = v => parseInt(v || 0).toLocaleString("en-US");

  const metric1 = selMetrics[0];
  const metric2 = selMetrics[1];
  const c1 = METRIC_COLORS[metric1] || "#5A94E6";
  const c2 = METRIC_COLORS[metric2] || "#D97736";

  // ADDED 2026-09-18: one decision for what the chart area shows, so a
  // failed fetch can never masquerade as a quiet day.
  const chartState = perfError ? "error"
                   : (perfLoading && !perfData.length) ? "loading"
                   : perfData.length ? "chart"
                   : "empty";

  return (
    <div className="layout">
      <Sidebar active="revenue"/>
      <div className="layout-main">
        <Header title="Revenue" userName={user?.name} hideSubtitle/>

        <div className="content">
          {/* ── Stat Cards ── */}
          <div className="rev-stats">
            <div className="rstat"><div className="rstat-lbl">Total Revenue</div><div className="rstat-val green">{fmtMoney(summary.total_revenue)}</div><div className="rstat-sub">{summary.affiliates_count || 0} affiliates</div></div>
            <div className="rstat"><div className="rstat-lbl">Total Clicks</div><div className="rstat-val blue">{fmtNum(summary.total_clicks)}</div><div className="rstat-sub">Across all affiliates</div></div>
            <div className="rstat"><div className="rstat-lbl">Conversions</div><div className="rstat-val orange">{fmtNum(summary.total_conversions)}</div><div className="rstat-sub">Total approved</div></div>
            <div className="rstat"><div className="rstat-lbl">Active Offers</div><div className="rstat-val purple">{fmtNum(summary.total_offers)}</div><div className="rstat-sub">With data in range</div></div>
          </div>

          {/* ── Performance Graph ── */}
          <div className="rev-perf-card">
            <div className="rev-perf-hd">
              <div>
                <span className="rev-perf-title">Performance Graph</span>
                {/* ADDED 2026-09-18 */}
                {perfLoading && perfData.length > 0 && (
                  <span style={{marginLeft:8,fontSize:11,color:"var(--text-muted)"}}>refreshing…</span>
                )}
              </div>
              <div style={{display:"flex",alignItems:"center",gap:8,marginLeft:"auto"}}>
                <MetricSelector selected={selMetrics} onChange={setSelMetrics}/>
                <div className="chart-type-btns">
                  <button className={`ct-btn ${chartType==="line"?"on":""}`} onClick={() => setChartType("line")}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
                  </button>
                  <button className={`ct-btn ${chartType==="bar"?"on":""}`} onClick={() => setChartType("bar")}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
                  </button>
                </div>
              </div>
            </div>

            {/* ADDED 2026-09-18: explicit chart states. No chart is drawn
                from data we did not successfully fetch. */}
            {chartState === "loading" && (
              <div style={{height:220,display:"flex",flexDirection:"column",alignItems:"center",
                           justifyContent:"center",gap:10,color:"var(--text-muted)",fontSize:13}}>
                <div className="loading-spinner"/>
                <span>Loading chart data…</span>
              </div>
            )}

            {chartState === "error" && (
              <div style={{height:220,display:"flex",flexDirection:"column",alignItems:"center",
                           justifyContent:"center",gap:8,fontSize:13,textAlign:"center",padding:"0 20px"}}>
                <div style={{fontSize:20}}>⚠</div>
                <div style={{color:"#B91C1C",fontWeight:600}}>Couldn't load chart data</div>
                <div style={{fontSize:12,color:"var(--text-muted)"}}>{perfError}</div>
                <button
                  onClick={() => buildPerfData(range, customStart, customEnd)}
                  style={{marginTop:4,padding:"6px 16px",borderRadius:6,
                          border:"1px solid var(--border-color, #E5E7EB)",
                          background:"#fff",cursor:"pointer",fontSize:12,fontWeight:600}}>
                  Retry
                </button>
              </div>
            )}

            {chartState === "empty" && (
              <div style={{height:220,display:"flex",alignItems:"center",justifyContent:"center",
                           color:"var(--text-muted)",fontSize:13}}>
                No data for this range.
              </div>
            )}

            {chartState === "chart" && (
              <ResponsiveContainer width="100%" height={220}>
                {chartType === "bar" ? (
                  <BarChart data={perfData} margin={{top:8,right:48,left:10,bottom:0}}>
                    <CartesianGrid strokeDasharray="0" stroke="#F0F1F3" vertical={false}/>
                    <XAxis dataKey="time" tick={{fontSize:11,fill:"#9CA3AF"}} tickLine={false} interval={Math.floor(perfData.length/8)} axisLine={{stroke:"#E5E7EB"}}/>
                    <YAxis yAxisId="l" tick={{fontSize:11,fill:c1}} tickLine={false} axisLine={false}/>
                    {metric2 && <YAxis yAxisId="r" orientation="right" tick={{fontSize:11,fill:c2}} tickLine={false} axisLine={false}/>}
                    <Tooltip content={<PerfTip/>}/>
                    <Bar yAxisId="l" dataKey={metric1} name={ALL_METRICS.find(m => m.key === metric1)?.label} fill={c1} fillOpacity={0.8} radius={[2,2,0,0]}/>
                    {metric2 && <Bar yAxisId="r" dataKey={metric2} name={ALL_METRICS.find(m => m.key === metric2)?.label} fill={c2} fillOpacity={0.8} radius={[2,2,0,0]}/>}
                  </BarChart>
                ) : (
                  <ComposedChart data={perfData} margin={{top:8,right:48,left:10,bottom:0}}>
                    <CartesianGrid strokeDasharray="0" stroke="#F0F1F3" vertical={false}/>
                    <XAxis dataKey="time" tick={{fontSize:11,fill:"#9CA3AF"}} tickLine={false} interval={Math.floor(perfData.length/8)} axisLine={{stroke:"#E5E7EB"}}/>
                    <YAxis yAxisId="l" tick={{fontSize:11,fill:c1}} tickLine={false} axisLine={false}/>
                    {metric2 && <YAxis yAxisId="r" orientation="right" tick={{fontSize:11,fill:c2}} tickLine={false} axisLine={false}/>}
                    <Tooltip content={<PerfTip/>}/>
                    <Area yAxisId="l" type="monotone" dataKey={metric1} name={ALL_METRICS.find(m => m.key === metric1)?.label} stroke={c1} strokeWidth={2.5} fill={c1+"33"} dot={false} connectNulls activeDot={{r:4}}/>
                    {metric2 && <Line yAxisId="r" type="monotone" dataKey={metric2} name={ALL_METRICS.find(m => m.key === metric2)?.label} stroke={c2} strokeWidth={2.5} dot={false} connectNulls activeDot={{r:4}}/>}
                  </ComposedChart>
                )}
              </ResponsiveContainer>
            )}

            {chartState === "chart" && (
              <div className="perf-leg" style={{padding:"4px 18px 12px",display:"flex",gap:16,flexWrap:"wrap"}}>
                <span className="pleg"><span className="pleg-line" style={{background:c1}}/>{ALL_METRICS.find(m => m.key === metric1)?.label}</span>
                {metric2 && <span className="pleg"><span className="pleg-line" style={{background:c2}}/>{ALL_METRICS.find(m => m.key === metric2)?.label}</span>}
              </div>
            )}
          </div>

          {/* ── Filter Toolbar ── */}
          <div className="rev-toolbar">
            {/* Row 1 — date range buttons */}
            <div className="rev-toolbar-row1">
              {["today","yesterday","month","last_month"].map(k => ({
                key: k,
                label: { today:"Today", yesterday:"Yesterday", month:"This Month", last_month:"Last Month" }[k]
              })).map(r => (
                <button key={r.key} className={`range-btn ${range===r.key?"active":""}`} onClick={() => setRange(r.key)}>{r.label}</button>
              ))}
              {/* Custom date picker */}
              <div className="drp-wrap">
                <button className={`range-btn ${range==="custom"?"active":""}`}
                  onClick={() => { setRange("custom"); setShowDRP(!showDRP); }}>
                  {range === "custom" && customStart ? `${customStart} → ${customEnd}` : "Custom"}
                </button>
                {showDRP && range === "custom" && (
                  <>
                    <div className="drp-overlay" onClick={() => setShowDRP(false)}/>
                    <DateRangePicker
                      start={customStart} end={customEnd}
                      onChange={(s, e) => { setCustomStart(s); setCustomEnd(e); }}
                      onClose={() => setShowDRP(false)}/>
                  </>
                )}
              </div>
              {range === "custom" && customStart && customEnd && (
                <button className="btn-go" onClick={() => { fetchRevenue(); setPage(1); }}>Go →</button>
              )}
            </div>
            {/* Row 2 — filters + search + sync */}
            <div className="rev-toolbar-row2">
              <select className="rev-select" value={affiliateF} onChange={e => { setAffiliateF(e.target.value); setPage(1); }}>
                <option value="all">All Affiliates</option>
                {affiliates.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
              <div className="groupby-btns">
                {[{k:"offer",l:"By Offer"},{k:"subid",l:"By Sub ID"}].map(g => (
                  <button key={g.k} className={`groupby-btn ${groupBy===g.k?"active":""}`}
                    onClick={() => { setGroupBy(g.k); setPage(1); }}>
                    {g.l}
                  </button>
                ))}
              </div>
              <div className="rev-search">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                <input type="text"
                  placeholder="Search by offer, sub ID... (comma = AND)"
                  value={search}
                  onChange={e => { setSearch(e.target.value); setPage(1); }}/>
                {search && <button onClick={() => { setSearch(""); setPage(1); }} style={{background:"none",border:"none",cursor:"pointer",color:"var(--text-muted)",fontSize:14,padding:0}}>✕</button>}
              </div>
              {/* Last synced indicator — driven by backend cron, no manual sync needed */}
              <div className="auto-sync-info" style={{marginLeft:"auto"}}>
                <span className="sync-dot"/>
                {lastSync
                  ? <span>Last synced: {edtTime(lastSync)} EDT</span>
                  : <span>Waiting for sync...</span>}
              </div>
            </div>
          </div>

          {error && <div className="rev-error">⚠ {error}</div>}

          {/* ── Table ── */}
          <div className="rev-table-wrap">
            <div className="rev-table-head">
              <span className="rev-count">{loading ? "Loading..." : `${filtered.length.toLocaleString()} records`}</span>
              <div style={{display:"flex",alignItems:"center",gap:12}}>
                <div style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:"var(--text-muted)"}}>
                  <span>Show</span>
                  <select className="pg-size-sel" value={pageSize}
                    onChange={e => { setPageSize(parseInt(e.target.value)); setPage(1); }}>
                    {PG_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
                    <option value={99999}>All</option>
                  </select>
                  <span>entries</span>
                </div>
              </div>
            </div>

            <div className="rev-table-scroll">
              <table className={`rev-table ${groupBy==="subid"?"tbl-sub":""}`}>
                <thead>
                  <tr>
                    <th className="sortable" onClick={() => handleSort("affiliate_name")}>Affiliate<SortIcon col="affiliate_name" sortCol={sortCol} sortDir={sortDir}/></th>
                    <th className="sortable th-offer" onClick={() => handleSort("offer_name")}>Offer<SortIcon col="offer_name" sortCol={sortCol} sortDir={sortDir}/></th>
                    {groupBy==="subid" && <th className="sortable th-subid" onClick={() => handleSort("sub_id")}>Sub ID<SortIcon col="sub_id" sortCol={sortCol} sortDir={sortDir}/></th>}
                    <th className="sortable th-right" onClick={() => handleSort("clicks")}>Clicks<SortIcon col="clicks" sortCol={sortCol} sortDir={sortDir}/></th>
                    {/* NOTE 2026-09-18: this column is REAL — it reads
                        revenue_cache.unique_clicks. Everflow rows carry a
                        value; Cake rows currently read 0 because Cake's
                        CampaignSummary isn't returning it under the field
                        name services/cake.js looks for. Under investigation
                        with diag_cake_unique.js — deliberately NOT faked. */}
                    <th className="sortable th-right" onClick={() => handleSort("unique_clicks")}>Uniq. Clicks<SortIcon col="unique_clicks" sortCol={sortCol} sortDir={sortDir}/></th>
                    <th className="sortable th-right" onClick={() => handleSort("conversions")}>CV<SortIcon col="conversions" sortCol={sortCol} sortDir={sortDir}/></th>
                    <th className="sortable th-right" onClick={() => handleSort("revenue")}>Revenue<SortIcon col="revenue" sortCol={sortCol} sortDir={sortDir}/></th>
                    <th className="sortable th-right" onClick={() => handleSort("cvr")}>CVR<SortIcon col="cvr" sortCol={sortCol} sortDir={sortDir}/></th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={groupBy==="subid"?9:8} className="td-loading"><div className="loading-spinner"/>Fetching data...</td></tr>
                  ) : paginated.length === 0 ? (
                    <tr><td colSpan={groupBy==="subid"?9:8} className="td-empty">No data found. Try adjusting the date range or filters.</td></tr>
                  ) : (
                    paginated.map((r, i) => (
                      <tr key={i}>
                        <td><span className="aff-name">{r.affiliate_name}</span></td>
                        {/* CHANGED 2026-09-23: tap/click opens the full name.
                            title= is kept so desktop hover still works as
                            before — the popup is an addition, not a swap. */}
                        <td className="td-offer"
                            title={r.offer_name}
                            style={{cursor:"pointer"}}
                            onClick={e => openCellPopup(e, "Offer", r.offer_name, false)}>
                          {r.offer_name}
                        </td>
                        {/* CHANGED 2026-09-23: this cell had NO tooltip at all
                            before — on desktop or mobile. Now it has both a
                            hover title and the tap popup. mono=true because a
                            Sub ID is an identifier and reads better fixed-width. */}
                        {groupBy==="subid" && (
                          <td className="td-id"
                              title={r.sub_id || ""}
                              style={{cursor:"pointer"}}
                              onClick={e => openCellPopup(e, "Sub ID", r.sub_id, true)}>
                            {r.sub_id || "—"}
                          </td>
                        )}
                        <td className="td-num-r">{fmtNum(r.clicks)}</td>
                        <td className="td-num-r">{fmtNum(r.unique_clicks)}</td>
                        <td className="td-num-r">{fmtNum(r.conversions)}</td>
                        <td className="td-money">{fmtMoney(r.revenue)}</td>
                        <td className="td-pct">{parseFloat(r.cvr || 0).toFixed(2)}%</td>
                      </tr>
                    ))
                  )}
                </tbody>
                {paginated.length > 0 && !loading && (
                  <tfoot>
                    <tr>
                      <td className="tf-lbl">TOTAL</td>
                      <td/>
                      {groupBy==="subid" && <td/>}
                      <td className="td-num-r tf">{fmtNum(filtered.reduce((a, b) => a + b.clicks,        0))}</td>
                      <td className="td-num-r tf">{fmtNum(filtered.reduce((a, b) => a + b.unique_clicks, 0))}</td>
                      <td className="td-num-r tf">{fmtNum(filtered.reduce((a, b) => a + b.conversions,   0))}</td>
                      <td className="td-money tf">{fmtMoney(filtered.reduce((a, b) => a + b.revenue,     0))}</td>
                      <td/>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>

            {/* ── Pagination ── */}
            {totalPages > 1 && pageSize !== 99999 && (
              <div className="pagination">
                <span className="pg-info">
                  Showing {((page - 1) * pageSize) + 1}–{Math.min(page * pageSize, filtered.length)} of {filtered.length.toLocaleString()}
                </span>
                <div className="pg-btns">
                  <button className="pg-btn" disabled={page === 1} onClick={() => setPage(1)}>«</button>
                  <button className="pg-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Previous</button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let p;
                    if      (totalPages <= 5)            p = i + 1;
                    else if (page <= 3)                  p = i + 1;
                    else if (page >= totalPages - 2)     p = totalPages - 4 + i;
                    else                                 p = page - 2 + i;
                    return <button key={p} className={`pg-btn ${page===p?"active":""}`} onClick={() => setPage(p)}>{p}</button>;
                  })}
                  {totalPages > 5 && page < totalPages - 2 && <span className="pg-dots">...</span>}
                  {totalPages > 5 && <button className={`pg-btn ${page===totalPages?"active":""}`} onClick={() => setPage(totalPages)}>{totalPages}</button>}
                  <button className="pg-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
                  <button className="pg-btn" disabled={page === totalPages} onClick={() => setPage(totalPages)}>»</button>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>

      {/* ADDED 2026-09-23 — the full-value popup.
          Rendered here, at the very outside of the page, on purpose: it is
          position:fixed, and a fixed element is measured against the nearest
          ancestor that has a CSS transform. Keeping it out here guarantees it
          is measured against the screen, so it lands exactly where the maths
          above says it should. */}
      {cellPopup && (
        <CellPopup
          label={cellPopup.label}
          text={cellPopup.text}
          mono={cellPopup.mono}
          anchor={cellPopup.anchor}
          onClose={closeCellPopup}
        />
      )}
    </div>
  );
}
